import React from 'react';
import { motion } from 'framer-motion';
import { FaLightbulb, FaPencilRuler, FaCode, FaRocket, FaCogs, FaClipboardCheck, FaPalette, FaLaptopCode, FaServer, FaArrowRight } from 'react-icons/fa';
import AnimatedSection from '../common/AnimatedSection';
import AnimatedButton from '../common/AnimatedButton';
import { technologies } from '../../data/technologies';

const ProcessSection = () => {
  return (
    <section className="py-24 bg-gray-900 text-white relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <svg className="absolute top-0 left-0 w-full h-full text-gray-800 opacity-30" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 1000" preserveAspectRatio="none">
          <defs>
            <pattern id="circuit" width="100" height="100" patternUnits="userSpaceOnUse">
              <path d="M 0 50 L 50 50 L 50 0" fill="none" stroke="currentColor" strokeWidth="1"/>
              <path d="M 50 100 L 50 50 L 100 50" fill="none" stroke="currentColor" strokeWidth="1"/>
              <circle cx="50" cy="50" r="3" fill="currentColor" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#circuit)" />
        </svg>
        
        <motion.div 
          className="absolute top-20 right-20 w-64 h-64 rounded-full bg-primary opacity-10 blur-3xl"
          animate={{ 
            y: [0, 40, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{ 
            duration: 15, 
            repeat: Infinity,
            repeatType: "reverse" 
          }}
        />
        
        <motion.div 
          className="absolute bottom-20 left-20 w-80 h-80 rounded-full bg-blue-500 opacity-10 blur-3xl"
          animate={{ 
            y: [0, -50, 0],
            scale: [1, 1.3, 1],
          }}
          transition={{ 
            duration: 18, 
            repeat: Infinity,
            repeatType: "reverse" 
          }}
        />
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <AnimatedSection className="text-center mb-16" direction="up">
          <motion.div 
            className="inline-block bg-primary bg-opacity-20 text-blue-300 font-bold px-4 py-2 rounded-full mb-4"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <div className="flex items-center">
              <FaCogs className="mr-2" />
              Méthodologie éprouvée
            </div>
          </motion.div>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            Notre <span className="text-primary">Processus</span> de Développement
          </h2>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Une approche structurée et transparente pour transformer votre vision en réalité digitale
          </p>
        </AnimatedSection>

        <div className="relative">
          {/* Connecting line */}
          <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-primary via-blue-400 to-accent hidden md:block"></div>
          
          {/* Process steps */}
          <div className="space-y-16 md:space-y-24 relative z-10">
            {/* Step 1 */}
            <div className="flex flex-col md:flex-row items-center">
              <motion.div 
                className="md:w-1/2 md:pr-16 mb-8 md:mb-0 text-center md:text-right"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true, margin: "-100px" }}
              >
                <h3 className="text-2xl font-bold mb-4 text-white">1. Analyse des besoins</h3>
                <p className="text-gray-300">
                  Nous commençons par une étude approfondie de votre projet pour comprendre vos objectifs, votre public cible et vos contraintes techniques. Cette phase inclut des entretiens détaillés et l'analyse de votre marché pour établir une stratégie optimale.
                </p>
                <div className="mt-4 flex flex-wrap justify-end gap-2">
                  <span className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-sm">Entretiens</span>
                  <span className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-sm">Étude de marché</span>
                  <span className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-sm">Benchmarking</span>
                </div>
              </motion.div>
              
              <div className="md:w-16 relative flex justify-center">
                <motion.div 
                  className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center z-20 shadow-lg shadow-primary/30"
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true, margin: "-100px" }}
                  whileHover={{ scale: 1.1, boxShadow: "0 0 20px rgba(59, 130, 246, 0.5)" }}
                >
                  <FaLightbulb className="text-2xl" />
                </motion.div>
                <div className="absolute top-0 left-0 w-16 h-16 bg-primary rounded-full opacity-20 blur-xl"></div>
              </div>
              
              <div className="md:w-1/2 md:pl-16">
                <motion.div 
                  className="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-xl"
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true, margin: "-100px" }}
                  whileHover={{ y: -5, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.3)" }}
                >
                  <div className="flex items-start mb-4">
                    <div className="w-8 h-8 rounded-full bg-blue-500 bg-opacity-20 flex items-center justify-center mr-3 mt-1">
                      <FaClipboardCheck className="text-blue-400" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white mb-2">Livrables</h4>
                      <ul className="text-gray-300 space-y-2">
                        <li className="flex items-center">
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mr-2"></div>
                          Cahier des charges détaillé
                        </li>
                        <li className="flex items-center">
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mr-2"></div>
                          Planning prévisionnel
                        </li>
                        <li className="flex items-center">
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mr-2"></div>
                          Devis personnalisé
                        </li>
                      </ul>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
            
            {/* Step 2 */}
            <div className="flex flex-col md:flex-row items-center">
              <div className="md:w-1/2 md:pr-16 mb-8 md:mb-0 md:order-1">
                <motion.div 
                  className="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-xl"
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true, margin: "-100px" }}
                  whileHover={{ y: -5, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.3)" }}
                >
                  <div className="flex items-start mb-4">
                    <div className="w-8 h-8 rounded-full bg-blue-500 bg-opacity-20 flex items-center justify-center mr-3 mt-1">
                      <FaPalette className="text-blue-400" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white mb-2">Livrables</h4>
                      <ul className="text-gray-300 space-y-2">
                        <li className="flex items-center">
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mr-2"></div>
                          Maquettes UI/UX
                        </li>
                        <li className="flex items-center">
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mr-2"></div>
                          Architecture technique
                        </li>
                        <li className="flex items-center">
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mr-2"></div>
                          Prototype interactif
                        </li>
                      </ul>
                    </div>
                  </div>
                </motion.div>
              </div>
              
              <div className="md:w-16 relative flex justify-center order-first md:order-2">
                <motion.div 
                  className="w-16 h-16 bg-blue-500 text-white rounded-full flex items-center justify-center z-20 shadow-lg shadow-blue-500/30"
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true, margin: "-100px" }}
                  whileHover={{ scale: 1.1, boxShadow: "0 0 20px rgba(59, 130, 246, 0.5)" }}
                >
                  <FaPencilRuler className="text-2xl" />
                </motion.div>
                <div className="absolute top-0 left-0 w-16 h-16 bg-blue-500 rounded-full opacity-20 blur-xl"></div>
              </div>
              
              <motion.div 
                className="md:w-1/2 md:pl-16 text-center md:text-left order-last md:order-3"
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true, margin: "-100px" }}
              >
                <h3 className="text-2xl font-bold mb-4 text-white">2. Conception</h3>
                <p className="text-gray-300">
                  Nous élaborons une solution sur mesure adaptée à vos besoins spécifiques. Notre équipe de designers crée des maquettes visuelles pendant que nos architectes définissent la structure technique optimale pour votre projet.
                </p>
                <div className="mt-4 flex flex-wrap justify-start gap-2">
                  <span className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-sm">UI/UX Design</span>
                  <span className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-sm">Wireframing</span>
                  <span className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-sm">Prototypage</span>
                </div>
              </motion.div>
            </div>
            
            {/* Step 3 */}
            <div className="flex flex-col md:flex-row items-center">
              <motion.div 
                className="md:w-1/2 md:pr-16 mb-8 md:mb-0 text-center md:text-right"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true, margin: "-100px" }}
              >
                <h3 className="text-2xl font-bold mb-4 text-white">3. Développement</h3>
                <p className="text-gray-300">
                  Nos développeurs créent votre solution avec les technologies les plus adaptées. Nous suivons une méthodologie agile avec des cycles de développement courts et des démonstrations régulières pour vous permettre de suivre l'avancement.
                </p>
                <div className="mt-4 flex flex-wrap justify-end gap-2">
                  <span className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-sm">Front-end</span>
                  <span className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-sm">Back-end</span>
                  <span className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-sm">API</span>
                  <span className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-sm">Tests</span>
                </div>
              </motion.div>
              
              <div className="md:w-16 relative flex justify-center">
                <motion.div 
                  className="w-16 h-16 bg-accent text-white rounded-full flex items-center justify-center z-20 shadow-lg shadow-accent/30"
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true, margin: "-100px" }}
                  whileHover={{ scale: 1.1, boxShadow: "0 0 20px rgba(236, 72, 153, 0.5)" }}
                >
                  <FaCode className="text-2xl" />
                </motion.div>
                <div className="absolute top-0 left-0 w-16 h-16 bg-accent rounded-full opacity-20 blur-xl"></div>
              </div>
              
              <div className="md:w-1/2 md:pl-16">
                <motion.div 
                  className="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-xl"
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true, margin: "-100px" }}
                  whileHover={{ y: -5, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.3)" }}
                >
                  <div className="flex items-start mb-4">
                    <div className="w-8 h-8 rounded-full bg-accent bg-opacity-20 flex items-center justify-center mr-3 mt-1">
                      <FaLaptopCode className="text-accent" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white mb-2">Technologies</h4>
                      <div className="grid grid-cols-3 gap-3 mt-3">
                        {technologies.slice(0, 6).map((tech, i) => (
                          <motion.div 
                            key={i} 
                            className="flex flex-col items-center justify-center p-2 bg-gray-900 rounded-lg"
                            whileHover={{ y: -2, scale: 1.05 }}
                          >
                            <div className="text-xl text-gray-300 mb-1">{tech.icon}</div>
                            <span className="text-xs text-gray-400">{tech.name}</span>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
            
            {/* Step 4 */}
            <div className="flex flex-col md:flex-row items-center">
              <div className="md:w-1/2 md:pr-16 mb-8 md:mb-0 md:order-1">
                <motion.div 
                  className="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-xl"
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true, margin: "-100px" }}
                  whileHover={{ y: -5, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.3)" }}
                >
                  <div className="flex items-start mb-4">
                    <div className="w-8 h-8 rounded-full bg-green-500 bg-opacity-20 flex items-center justify-center mr-3 mt-1">
                      <FaServer className="text-green-400" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white mb-2">Services inclus</h4>
                      <ul className="text-gray-300 space-y-2">
                        <li className="flex items-center">
                          <div className="w-1.5 h-1.5 rounded-full bg-green-400 mr-2"></div>
                          Mise en production sécurisée
                        </li>
                        <li className="flex items-center">
                          <div className="w-1.5 h-1.5 rounded-full bg-green-400 mr-2"></div>
                          Formation utilisateur
                        </li>
                        <li className="flex items-center">
                          <div className="w-1.5 h-1.5 rounded-full bg-green-400 mr-2"></div>
                          Support technique (30 jours)
                        </li>
                        <li className="flex items-center">
                          <div className="w-1.5 h-1.5 rounded-full bg-green-400 mr-2"></div>
                          Documentation complète
                        </li>
                      </ul>
                    </div>
                  </div>
                </motion.div>
              </div>
              
              <div className="md:w-16 relative flex justify-center order-first md:order-2">
                <motion.div 
                  className="w-16 h-16 bg-green-500 text-white rounded-full flex items-center justify-center z-20 shadow-lg shadow-green-500/30"
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true, margin: "-100px" }}
                  whileHover={{ scale: 1.1, boxShadow: "0 0 20px rgba(34, 197, 94, 0.5)" }}
                >
                  <FaRocket className="text-2xl" />
                </motion.div>
                <div className="absolute top-0 left-0 w-16 h-16 bg-green-500 rounded-full opacity-20 blur-xl"></div>
              </div>
              
              <motion.div 
                className="md:w-1/2 md:pl-16 text-center md:text-left order-last md:order-3"
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true, margin: "-100px" }}
              >
                <h3 className="text-2xl font-bold mb-4 text-white">4. Déploiement & Support</h3>
                <p className="text-gray-300">
                  Nous mettons en ligne votre projet et assurons son bon fonctionnement. Notre équipe vous accompagne dans la prise en main de votre solution et reste disponible pour répondre à vos questions et assurer la maintenance.
                </p>
                <div className="mt-4 flex flex-wrap justify-start gap-2">
                  <span className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-sm">Hébergement</span>
                  <span className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-sm">Sécurité</span>
                  <span className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-sm">Maintenance</span>
                  <span className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-sm">Formation</span>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
        
        <div className="text-center mt-20">
          <AnimatedButton 
            to="/contact" 
            type="primary"
            size="lg"
            className="px-8 py-4"
          >
            <div className="flex items-center justify-center">
              <span>Démarrer votre projet</span>
              <FaArrowRight className="ml-2" />
            </div>
          </AnimatedButton>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;